import { Button } from '@eriko/ui/src/components/ui/button';
import { Input } from '@eriko/ui/src/components/ui/input';
import { Label } from '@eriko/ui/src/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@eriko/ui/src/components/ui/select';
import { Trash2, Plus } from 'lucide-react';
import type { MatchedObjectType } from '@eriko/shared-types';
import { formatCurrency } from '@eriko/shared-types';

export interface Allocation {
  objectType: MatchedObjectType;
  objectId: string;
  amount: string;
}

const ALLOCATION_TYPES: MatchedObjectType[] = ['BILL', 'INVOICE', 'RECEIPT', 'EXPENSE'];

interface Props {
  allocations: Allocation[];
  onChange: (next: Allocation[]) => void;
  targetAmount: number;
  /** ISO 4217 code (e.g. "USD", "GBP"). Defaults to USD if unset. */
  currency?: string;
}

export function AllocationBuilder({ allocations, onChange, targetAmount, currency }: Props) {
  const sum = allocations.reduce((acc, a) => acc + (parseFloat(a.amount) || 0), 0);
  const diff = Math.abs(sum - targetAmount);
  // engine uses $1/item tolerance
  const withinTolerance = allocations.length > 0 && diff <= 1 * allocations.length;

  const update = (idx: number, patch: Partial<Allocation>) => {
    onChange(allocations.map((a, i) => (i === idx ? { ...a, ...patch } : a)));
  };
  const add = () =>
    onChange([...allocations, { objectType: 'INVOICE', objectId: '', amount: '0.00' }]);
  const remove = (idx: number) => onChange(allocations.filter((_, i) => i !== idx));

  return (
    <div className="space-y-3">
      <Label>Allocations</Label>
      <div className="space-y-2">
        {allocations.map((a, i) => (
          <div key={i} className="flex items-end gap-2">
            <div className="w-32">
              <Select
                value={a.objectType}
                onValueChange={(v) => update(i, { objectType: v as MatchedObjectType })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ALLOCATION_TYPES.map((t) => (
                    <SelectItem key={t} value={t}>
                      {t}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Input
                value={a.objectId}
                onChange={(e) => update(i, { objectId: e.target.value })}
                placeholder="Object id"
              />
            </div>
            <div className="w-28">
              <Input
                value={a.amount}
                onChange={(e) => update(i, { amount: e.target.value })}
                placeholder="0.00"
                className="text-right font-mono"
              />
            </div>
            <Button variant="ghost" size="sm" onClick={() => remove(i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
      <Button variant="outline" size="sm" onClick={add}>
        <Plus className="h-4 w-4 mr-1" /> Add allocation
      </Button>

      <div className="flex justify-between rounded-md bg-surface-50 px-3 py-2 text-sm">
        <span className="text-surface-500">
          Sum: <span className="font-mono">{formatCurrency(sum, 2, currency)}</span> / target{' '}
          <span className="font-mono">{formatCurrency(targetAmount, 2, currency)}</span>
        </span>
        <span
          className={
            withinTolerance ? 'text-emerald-700 font-medium' : 'text-amber-700'
          }
        >
          {withinTolerance ? '✓ Within tolerance' : `Δ ${formatCurrency(diff, 2, currency)}`}
        </span>
      </div>
    </div>
  );
}
